import {useEffect, useRef, useState} from "react";
import {
    Autocomplete, Avatar, Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Switch,
    TextField, Typography
} from "@mui/material";
import {
    Close as CloseIcon,
    Search as SearchIcon,
    SupervisorAccount as ManagerIcon
} from "@mui/icons-material";
import {getCompanyUsersWithoutRoles} from "../../services/UserService";
import {filterUserCandidates, promotableCandidates} from "../../utils/userCandidates";
import type {UserCandidate} from "../../utils/userCandidates";
import PhoneInput, {isValidPhoneNumber} from "../shared/PhoneInput";
import {RTL} from "../../cache/CacheRtl";
import {colors, borders} from "../ui/colors";

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: borders.radius.md,
        backgroundColor: colors.white,
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary, borderWidth: "2px" }
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.border }
};

const labelSx = { fontSize: "13px", fontWeight: 500, color: colors.textSecondary, marginBottom: "6px" };

/** Deliberately permissive: catches typos ("a@b", "a b@c.com"), not exotic-but-legal addresses. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/** Minimum the server accepts; kept here so the form can say so before a round-trip. */
const MIN_PASSWORD_LENGTH = 6;

/** Which fields the form validates. Each maps to at most one visible message at a time. */
type FieldName = "name" | "email" | "phoneNumber" | "password" | "secondPassword";

/** `true` once a field has been blurred — errors stay hidden until the user leaves the field. */
type TouchedMap = Partial<Record<FieldName, boolean>>;

/** Async "already taken" verdicts, keyed by the value they were fetched for (so they can't go stale). */
type AvailabilityState = {
    email: {value: string; exists: boolean} | null;
    phoneNumber: {value: string; exists: boolean} | null;
    name: {value: string; exists: boolean} | null;
};

/** The identifiers the async probe can check. `name` is only ever checked when opted in via prop. */
type CheckedField = "email" | "phoneNumber" | "name";

export type OwnerFormValues = {
    id: number | null;
    companyId: number | null;
    name: string;
    email: string;
    phoneNumber: string;
    userId: string;
    password: string;
    secondPassword: string;
};

/**
 * What the caller's `handleSubmit` receives. `password`/`userId` are nulled rather than sent as the
 * empty-string sentinels the form uses internally, and `secondPassword` is stripped — the confirm
 * field is a UI concern and never part of the payload.
 */
export type OwnerSubmitPayload = Omit<OwnerFormValues, "password" | "userId" | "secondPassword"> & {
    password: string | null;
    userId: string | null;
    secondPassword: undefined;
};

/** Result of the caller-supplied "is this identifier taken?" probe. All flags are optional. */
export type AvailabilityCheck = (args: {
    email?: string | null;
    phoneNumber?: string | null;
    name?: string | null;
}) => Promise<{emailExists?: boolean; phoneExists?: boolean; nameExists?: boolean}>;

export type AddOwnerDialogProps = {
    open: boolean;
    close: () => void;
    owner?: {id?: number | null; name?: string; email?: string; phoneNumber?: string} | null;
    companyId?: number | null;
    handleSubmit: (values: OwnerSubmitPayload) => void;
    title?: string;
    titleIcon?: React.ReactNode;
    children?: React.ReactNode;
    // UserCandidate, not a narrower inline shape: the picker searches on `email` and filters on
    // `enabled`, so a type omitting them would type-check a list the picker cannot actually use.
    // Every field stays optional to match the server DTO (generated `User` has no required fields).
    availableUsers?: UserCandidate[] | null;
    defaultExistingUser?: boolean;
    allowExistingUser?: boolean;
    checkAvailability?: AvailabilityCheck | null;
    // Opt-in: also validate that the full name isn't already taken (via checkAvailability's `name`
    // flag), on blur + as a submit gate. Off by default so the company-owner flow is unaffected — only
    // personas with a name-uniqueness rule (Company Group Administrator) turn it on.
    validateNameUniqueness?: boolean;
};

// Shared "add/edit manager" dialog. Originally built for a company's owners (ADMIN of one
// company) and reused for the Company Group Administrator persona. Optional props let callers
// customize it without forking:
//   - title / titleIcon: header overrides (default: הוספת/עריכת מנהל + ManagerIcon)
//   - children: extra content rendered at the bottom of the body (e.g. an impersonation picker)
//   - availableUsers: pre-supplied "existing user" options; when provided the internal
//       getUsersWithoutRoles fetch is skipped (used by the group-admin flow, which has no
//       company context and sources its users elsewhere)
//   - defaultExistingUser: initial state of the "משתמש קיים" toggle (default: !owner)
//   - checkAvailability: optional probe that answers "is this email/phone already taken?". When
//       supplied, the dialog runs it on blur and blocks submit on a collision. Left out, the form
//       still validates format / password match locally — the async check is purely additive, so
//       callers without a suitable endpoint keep their old behavior.
//
// Validation runs on BLUR, not per keystroke: a message like "email is invalid" while the user is
// still mid-word is noise. Submit stays disabled while anything is invalid, and the async
// availability verdicts are keyed by the value they were fetched for so a slow in-flight response
// can never be applied to a newer value.
const AddOwnerDialog = ({
    open,
    close,
    owner,
    companyId,
    handleSubmit,
    title,
    titleIcon,
    children,
    availableUsers,
    defaultExistingUser,
    allowExistingUser = true,
    checkAvailability = null,
    validateNameUniqueness = false,
}: AddOwnerDialogProps) => {
    const buildInitialOwner = (): OwnerFormValues => ({
        id: owner?.id ?? null,
        companyId: companyId ?? null,
        name: owner ? owner.name ?? "" : "",
        email: owner ? owner.email ?? "" : "",
        phoneNumber: owner ? owner.phoneNumber ?? "" : "",
        userId: "null",
        password: "",
        secondPassword: "",
    });

    const [exsistingUser, setExsistingUser] = useState(allowExistingUser ? (defaultExistingUser ?? !owner) : false);
    const [avilibleUsers, setAvilibleUsers] = useState<UserCandidate[]>(promotableCandidates(availableUsers));
    const [companyOwner, setCompanyOwner] = useState<OwnerFormValues>(buildInitialOwner);
    const [touched, setTouched] = useState<TouchedMap>({});
    const [availability, setAvailability] = useState<AvailabilityState>({email: null, phoneNumber: null, name: null});
    const [checking, setChecking] = useState<{email: boolean; phoneNumber: boolean; name: boolean}>({
        email: false, phoneNumber: false, name: false,
    });

    // Guards the async check against out-of-order responses AND against setting state after the
    // dialog closes/unmounts: every request records the value it was issued for, and a response is
    // only applied while that value is still the latest one requested.
    const latestRequest = useRef<{email: string; phoneNumber: string; name: string}>({email: "", phoneNumber: "", name: ""});

    // Reset the form each time the dialog opens so stale input never leaks between sessions.
    useEffect(() => {
        if (!open) return;
        setExsistingUser(allowExistingUser ? (defaultExistingUser ?? !owner) : false);
        setCompanyOwner(buildInitialOwner());
        setTouched({});
        setAvailability({email: null, phoneNumber: null, name: null});
        setChecking({email: false, phoneNumber: false, name: false});
        latestRequest.current = {email: "", phoneNumber: "", name: ""};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Existing-user options: use the caller-supplied list when given, otherwise fetch the
    // company's role-less users (original behavior). Group admins pass availableUsers and have
    // no companyId, so the fetch is skipped.
    useEffect(() => {
        if (!open) return;
        // promotableCandidates on BOTH branches: a disabled user cannot hold a session, so promoting
        // one produces staff who cannot log in. The server already excludes them from the fetched
        // pool, but the caller-supplied list is just a prop — nothing guarantees where it came from.
        if (availableUsers) {
            setAvilibleUsers(promotableCandidates(availableUsers));
            return;
        }
        // System variant on purpose: this is a SYSTEM_ADMIN flow that picks candidates in an
        // ARBITRARY company (chosen via the company param), not the caller's own.
        //
        // The role asked for is the one this dialog GRANTS (ADMIN) — the server returns company
        // users who do NOT already hold it, i.e. the promote-able pool. Asking for SYSTEM_ADMIN
        // excluded nobody in a normal company, so the picker listed the company's existing admins.
        getCompanyUsersWithoutRoles("ADMIN", companyId).then(res => {
            setAvilibleUsers(promotableCandidates(res.data));
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, availableUsers]);

    const updateField = (field: keyof OwnerFormValues, value: string) => {
        setCompanyOwner(prv => ({...prv, [field]: value}));
        // Editing an identifier invalidates any verdict we hold for it — otherwise a stale "taken"
        // would keep the form blocked after the user typed a different value.
        if (field === "email" || field === "phoneNumber" || field === "name") {
            setAvailability(prv => ({...prv, [field]: null}));
        }
    };

    const handleClose = () => {
        close();
    };

    /**
     * Ask the caller's probe whether this identifier is taken, then record the verdict against the
     * exact value queried. `latestRequest` makes the write conditional, so an earlier slow response
     * can't overwrite a newer one (or land after the dialog reset).
     */
    const runAvailabilityCheck = async (field: CheckedField, value: string) => {
        if (!checkAvailability || !value) return;
        latestRequest.current = {...latestRequest.current, [field]: value};
        setChecking(prv => ({...prv, [field]: true}));
        try {
            const result = await checkAvailability({[field]: value});
            if (latestRequest.current[field] !== value) return; // superseded
            const exists = field === "email"
                ? result?.emailExists === true
                : field === "phoneNumber"
                    ? result?.phoneExists === true
                    : result?.nameExists === true;
            setAvailability(prv => ({...prv, [field]: {value, exists}}));
        } catch {
            // A failed probe must not block the form: leave the verdict unset and let the server's
            // own duplicate rejection be the backstop on submit.
            if (latestRequest.current[field] === value) {
                setAvailability(prv => ({...prv, [field]: null}));
            }
        } finally {
            if (latestRequest.current[field] === value) {
                setChecking(prv => ({...prv, [field]: false}));
            }
        }
    };

    const markTouched = (field: FieldName) => setTouched(prv => ({...prv, [field]: true}));

    const handleBlur = (field: FieldName) => {
        markTouched(field);
        if (field === "name" && validateNameUniqueness) {
            const name = companyOwner.name.trim();
            if (name) runAvailabilityCheck("name", name);
        }
        if (field === "email") {
            const email = companyOwner.email.trim();
            if (email && EMAIL_PATTERN.test(email)) runAvailabilityCheck("email", email);
        }
        if (field === "phoneNumber") {
            const phone = companyOwner.phoneNumber;
            if (phone && isValidPhoneNumber(phone)) runAvailabilityCheck("phoneNumber", phone);
        }
    };

    /**
     * The single source of truth for field validity — used both for the messages under each field and
     * for the submit gate, so what the user sees and what the button does can never disagree.
     * Password rules apply only to the create flow (an existing owner keeps its password unless a new
     * one is typed).
     */
    const errors = (): Partial<Record<FieldName, string>> => {
        const result: Partial<Record<FieldName, string>> = {};
        const isNew = !companyOwner.id;
        const email = companyOwner.email.trim();

        const name = companyOwner.name.trim();
        if (!name) {
            result.name = "שדה חובה";
        } else if (validateNameUniqueness
            && availability.name?.value === name && availability.name.exists) {
            result.name = "השם כבר קיים במערכת";
        }

        if (!email) {
            result.email = "שדה חובה";
        } else if (!EMAIL_PATTERN.test(email)) {
            result.email = "כתובת אימייל לא תקינה";
        } else if (availability.email?.value === email && availability.email.exists) {
            result.email = "כתובת האימייל כבר קיימת במערכת";
        }

        if (!companyOwner.phoneNumber) {
            result.phoneNumber = "שדה חובה";
        } else if (!isValidPhoneNumber(companyOwner.phoneNumber)) {
            result.phoneNumber = "מספר טלפון לא תקין";
        } else if (availability.phoneNumber?.value === companyOwner.phoneNumber
            && availability.phoneNumber.exists) {
            result.phoneNumber = "מספר הטלפון כבר קיים במערכת";
        }

        // On create a password is required; on edit it's optional but must still be valid if typed.
        if (isNew || companyOwner.password || companyOwner.secondPassword) {
            if (!companyOwner.password) {
                result.password = "שדה חובה";
            } else if (companyOwner.password.length < MIN_PASSWORD_LENGTH) {
                result.password = `הסיסמה חייבת להכיל לפחות ${MIN_PASSWORD_LENGTH} תווים`;
            }

            if (!companyOwner.secondPassword) {
                result.secondPassword = "שדה חובה";
            } else if (companyOwner.password !== companyOwner.secondPassword) {
                result.secondPassword = "הסיסמאות אינן תואמות";
            }
        }

        return result;
    };

    const fieldErrors = errors();

    /** Show an error only after the field was blurred — no red text on a form the user just opened. */
    const errorFor = (field: FieldName) => (touched[field] ? fieldErrors[field] : undefined);

    const isSubmitDisabled = () => {
        if (exsistingUser) return companyOwner.userId === "null";
        // Block while a check is still in flight so submit can't race past a pending "taken" verdict.
        if (checking.email || checking.phoneNumber || checking.name) return true;
        return Object.keys(fieldErrors).length > 0;
    };

    /** Mark everything touched, so a click on a disabled-looking form reveals what's wrong. */
    const revealAllErrors = () => setTouched({
        name: true, email: true, phoneNumber: true, password: true, secondPassword: true,
    });

    const onSubmit = () => {
        if (isSubmitDisabled()) {
            revealAllErrors();
            return;
        }
        handleSubmit({
            ...companyOwner,
            email: companyOwner.email.trim(),
            name: companyOwner.name.trim(),
            password: companyOwner.password === "" ? null : companyOwner.password,
            userId: companyOwner.userId === "null" ? null : companyOwner.userId,
            secondPassword: undefined,
        });
    };

    /** Small helper so every text field renders its message the same way. */
    const helperTextFor = (field: FieldName) => {
        const message = errorFor(field);
        if (!message) return null;
        return (
            <Typography sx={{color: colors.danger ?? "#d32f2f", fontSize: "12px", marginTop: "4px"}}>
                {message}
            </Typography>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: borders.radius.lg,
                    minWidth: {xs: "90%", sm: "480px"},
                    maxWidth: {xs: "95%", md: "600px"},
                    maxHeight: {xs: "90vh", md: "85vh"},
                    overflowY: "auto"
                }
            }}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: colors.bg,
                        borderBottom: borders.default
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            {titleIcon || <ManagerIcon sx={{color: colors.textSecondary, fontSize: "20px"}} />}
                            <Typography sx={{ fontWeight: 600, fontSize: "16px", color: colors.textPrimary }}>
                                {title || (owner?.id ? "עריכת מנהל" : "הוספת מנהל")}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{ color: colors.textMuted, "&:hover": { backgroundColor: colors.bgHover, color: colors.textPrimary } }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <DialogContent sx={{padding: "24px 20px"}}>
                        {/* Existing user toggle — only for new owners */}
                        {!companyOwner.id && allowExistingUser && (
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "10px 14px",
                                backgroundColor: colors.bg,
                                border: borders.default,
                                borderRadius: borders.radius.md,
                                marginBottom: "20px"
                            }}>
                                <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary}}>
                                    משתמש קיים
                                </Typography>
                                <Switch
                                    checked={exsistingUser}
                                    onChange={() => setExsistingUser(prv => !prv)}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {color: colors.primary},
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: colors.primary}
                                    }}
                                />
                            </Box>
                        )}

                        {/* Existing user selector */}
                        {allowExistingUser && exsistingUser && !companyOwner.id && (
                            <Box sx={{marginBottom: "20px"}}>
                                <Typography sx={labelSx}>בחר משתמש</Typography>
                                {/*
                                  * A searchable Autocomplete rather than the plain dropdown this used
                                  * to be. The list is every company user who does not already hold the
                                  * role, which in a real gym is hundreds of people — scrolling a
                                  * <select> to find one is not a search, and the same picker in
                                  * "הוספת מאמן" had already been through this. Same matcher, so typing
                                  * an email finds a user whose name you can't spell.
                                  */}
                                <Autocomplete
                                    id="user"
                                    options={avilibleUsers}
                                    value={avilibleUsers.find(u => String(u.id) === String(companyOwner.userId)) ?? null}
                                    getOptionLabel={(option) => option?.name || ""}
                                    filterOptions={(options, {inputValue}) =>
                                        filterUserCandidates(options, inputValue)
                                    }
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    // "null" rather than "" on clear: the rest of this form treats the
                                    // string "null" as "no user chosen" (see buildInitialOwner), and an
                                    // empty string would read as a chosen user with a blank id.
                                    onChange={(_, value) => updateField("userId", value?.id != null ? String(value.id) : "null")}
                                    noOptionsText="לא נמצאו משתמשים"
                                    sx={{
                                        ...inputSx,
                                        "& .MuiAutocomplete-popupIndicator": {color: colors.textMuted},
                                    }}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} key={option.id} sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            padding: "8px 12px !important"
                                        }}>
                                            <Avatar sx={{
                                                width: 28, height: 28,
                                                backgroundColor: colors.primaryLight,
                                                fontSize: "12px",
                                                color: colors.primary
                                            }}>
                                                {option.name?.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{fontSize: "14px", fontWeight: 500, color: colors.textPrimary}}>
                                                    {option.name}
                                                </Typography>
                                                {option.email && (
                                                    <Typography sx={{fontSize: "12px", color: colors.textMuted}}>
                                                        {option.email}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="חפש משתמש..."
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <SearchIcon sx={{color: colors.textMuted, fontSize: 20, mr: 0.5}}/>
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        )}

                        {/* New user form */}
                        {!exsistingUser && (
                            <Box sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
                                gap: "16px",
                            }}>
                                <Box>
                                    <Typography sx={labelSx}>*שם מלא</Typography>
                                    <TextField sx={inputSx} fullWidth size="small"
                                        value={companyOwner.name}
                                        onChange={(e) => updateField("name", e.target.value)}
                                        onBlur={() => handleBlur("name")}
                                        error={!!errorFor("name")}
                                        inputProps={{"aria-label": "שם מלא"}}
                                        InputProps={{
                                            endAdornment: checking.name
                                                ? <CircularProgress size={16} aria-label="בודק שם" />
                                                : null,
                                        }}
                                        variant="outlined" />
                                    {helperTextFor("name")}
                                </Box>

                                <Box>
                                    <Typography sx={labelSx}>*אימייל</Typography>
                                    <TextField sx={{...inputSx, direction: "ltr"}} fullWidth size="small"
                                        value={companyOwner.email}
                                        onChange={(e) => updateField("email", e.target.value)}
                                        onBlur={() => handleBlur("email")}
                                        error={!!errorFor("email")}
                                        inputProps={{"aria-label": "אימייל"}}
                                        InputProps={{
                                            endAdornment: checking.email
                                                ? <CircularProgress size={16} aria-label="בודק אימייל" />
                                                : null,
                                        }}
                                        variant="outlined" />
                                    {helperTextFor("email")}
                                </Box>

                                <Box>
                                    <Typography sx={labelSx}>*טלפון</Typography>
                                    <Box onBlur={() => handleBlur("phoneNumber")}>
                                        <PhoneInput sx={inputSx} value={companyOwner.phoneNumber}
                                            error={!!errorFor("phoneNumber")}
                                            helperText={errorFor("phoneNumber")}
                                            onChange={(value: string) => updateField("phoneNumber", value)} />
                                    </Box>
                                    {checking.phoneNumber && (
                                        <Typography sx={{fontSize: "12px", color: colors.textMuted, marginTop: "4px"}}>
                                            בודק מספר טלפון…
                                        </Typography>
                                    )}
                                </Box>

                                {!companyOwner.id && (
                                    <>
                                        <Box>
                                            <Typography sx={labelSx}>*סיסמה</Typography>
                                            <TextField sx={inputSx} fullWidth size="small" type="password"
                                                value={companyOwner.password}
                                                onChange={(e) => updateField("password", e.target.value)}
                                                onBlur={() => handleBlur("password")}
                                                error={!!errorFor("password")}
                                                inputProps={{"aria-label": "סיסמה"}}
                                                variant="outlined" />
                                            {helperTextFor("password")}
                                        </Box>
                                        <Box>
                                            <Typography sx={labelSx}>*סיסמה בשנית</Typography>
                                            <TextField sx={inputSx} fullWidth size="small" type="password"
                                                value={companyOwner.secondPassword}
                                                onChange={(e) => updateField("secondPassword", e.target.value)}
                                                onBlur={() => handleBlur("secondPassword")}
                                                error={!!errorFor("secondPassword")}
                                                inputProps={{"aria-label": "סיסמה בשנית"}}
                                                variant="outlined" />
                                            {helperTextFor("secondPassword")}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        )}

                        {/* Extra content slot (e.g. impersonation companies for a group admin) */}
                        {children}
                    </DialogContent>

                    {/* Footer */}
                    <Box sx={{
                        display: "flex", justifyContent: "flex-start", gap: 1.5,
                        padding: "16px 20px", borderTop: borders.default, backgroundColor: colors.bg
                    }}>
                        <Button
                            disabled={isSubmitDisabled()}
                            onClick={onSubmit}
                            variant="contained"
                            sx={{
                                backgroundColor: colors.primary, color: colors.white, fontSize: "13px", fontWeight: 600,
                                padding: "8px 24px", borderRadius: borders.radius.md, textTransform: "none",
                                "&:hover": { backgroundColor: colors.primaryDark },
                                "&.Mui-disabled": { backgroundColor: colors.border, color: colors.textMuted }
                            }}>
                            שמור
                        </Button>
                        <Button onClick={handleClose} variant="outlined"
                            sx={{
                                borderColor: colors.border, color: colors.textSecondary, fontSize: "13px", fontWeight: 500,
                                padding: "8px 20px", borderRadius: borders.radius.md, textTransform: "none",
                                "&:hover": { borderColor: colors.textSecondary, backgroundColor: colors.bgHover }
                            }}>
                            ביטול
                        </Button>
                    </Box>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default AddOwnerDialog;
